import styled, { css } from 'styled-components'

// import { DatePickerNewProps } from '.'

// ─── DayButton prop types ─────────────────────────────────────────────────────

export type DayButtonProps = {
  isSelected: boolean
  isToday: boolean
  isDisabled: boolean
}

// ─── Wrapper ──────────────────────────────────────────────────────────────────

export const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`

// ─── PopoverContent ───────────────────────────────────────────────────────────

export const PopoverContent = styled.div`
  ${({ theme }) => css`
    padding: 16px;
    min-width: 280px;
    border-radius: ${theme.ds.typography.border.radius.small};
    margin-top: 4px;
  `}
`

// ─── DropdownsRow ─────────────────────────────────────────────────────────────

export const DropdownsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 12px;
`

// ─── Shared select base ───────────────────────────────────────────────────────

const selectBase = css`
  ${({ theme }) => css`
    flex: 1;
    height: 36px;
    padding: 0 12px;
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size.xsmall};
    font-weight: ${theme.ds.typography.weight.normal};
    color: ${theme.ds.colors.neutral900};
    background-color: ${theme.ds.colors.white};
    border: 1px solid ${theme.ds.colors.neutral300};
    border-radius: ${theme.ds.typography.border.radius.xxsmall};
    outline: none;
    cursor: pointer;
    -moz-appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,<svg height='10px' width='10px' viewBox='0 0 16 16' fill='%23000000' xmlns='http://www.w3.org/2000/svg'><path d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/></svg>");
    background-repeat: no-repeat;
    background-position: calc(100% - 10px) center;

    &:focus {
      border-color: ${theme.ds.colors.primary500};
    }
  `}
`

// ─── MonthSelect ──────────────────────────────────────────────────────────────

export const MonthSelect = styled.select`
  ${selectBase}
`

// ─── YearSelect ───────────────────────────────────────────────────────────────

export const YearSelect = styled.select`
  ${selectBase}
`

// ─── WeekdaysRow ──────────────────────────────────────────────────────────────

export const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
`

// ─── WeekdayCell ─────────────────────────────────────────────────────────────

export const WeekdayCell = styled.span`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size.xxsmall};
    font-weight: ${theme.ds.typography.weight.semiBold};
    color: ${theme.ds.colors.neutral500};
    padding: 4px 0;
    text-align: center;
  `}
`

// ─── DayGridWrapper ───────────────────────────────────────────────────────────

export const DayGridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

// ─── DayButton ────────────────────────────────────────────────────────────────

export const DayButton = styled.button<DayButtonProps>`
  ${({ theme, isSelected, isToday, isDisabled }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 1;
    min-width: 32px;
    min-height: 32px;
    border: none;
    border-radius: ${theme.ds.typography.border.radius.rounded};
    background-color: ${theme.ds.colors.transparent};
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size.xsmall};
    font-weight: ${theme.ds.typography.weight.normal};
    color: ${theme.ds.colors.neutral900};
    cursor: pointer;
    transition: background-color 0.15s ease-in-out;
    outline: none;

    &:hover {
      background-color: ${theme.ds.colors.primary50};
      color: ${theme.ds.colors.primary700};
    }

    ${isToday &&
    !isSelected &&
    css`
      border: 1px solid ${theme.ds.colors.primary400};
      color: ${theme.ds.colors.primary600};
      font-weight: ${theme.ds.typography.weight.semiBold};
    `}

    ${isSelected &&
    css`
      background-color: ${theme.ds.colors.primary600};
      color: ${theme.ds.colors.white};
      font-weight: ${theme.ds.typography.weight.bold};

      &:hover {
        background-color: ${theme.ds.colors.primary700};
        color: ${theme.ds.colors.white};
      }
    `}

    ${isDisabled &&
    css`
      color: ${theme.ds.colors.neutral300};
      cursor: not-allowed;
      pointer-events: none;

      &:hover {
        background-color: ${theme.ds.colors.transparent};
        color: ${theme.ds.colors.neutral300};
      }
    `}
  `}
`

// ─── EmptyCell ────────────────────────────────────────────────────────────────

export const EmptyCell = styled.span`
  display: block;
  min-width: 32px;
  min-height: 32px;
`
